import {
  SILENT_AUDIO_DWELL_MS,
  SILENT_AUDIO_RECAPTURE_COOLDOWN_MS,
  classifySilentAudioSample,
  isSilentAttachedPastDwell,
  type SilentAudioSample,
} from '@playcastdotio/PeerCore';
import * as Sentry from '@sentry/react';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';

const DIAG = '[AUDIO-BUG-12009:SilentAudioWatchdog]';
/** Sample retention window; computed at use-time so circular PeerCore init cannot latch NaN. */
function sampleWindowMs(): number {
  return SILENT_AUDIO_DWELL_MS + 2_000;
}

export type SilentAudioWatchdogDeps = {
  /** Re-run AudioCaster getUserMedia capture. */
  recaptureGameAudio: () => Promise<MediaStreamTrack | null>;
  /**
   * Re-assert AudioCaster as the default render device so game audio has a path into
   * the capture endpoint. Optional — recapture alone still runs when omitted.
   */
  ensureAudioCasterDefault?: () => Promise<void>;
  nowMs?: () => number;
};

/**
 * Host-side response to silent-but-attached game audio (AUDIO-BUG-12009).
 *
 * Observes guest `clientStats` audioLevel/bitrate. After the shared dwell, re-asserts the
 * AudioCaster render path and re-captures. Logs/Sentry use the existing AUDIO-BUG-12009 tag.
 */
export class SilentAudioWatchdog {
  private readonly samplesByPeer = new Map<string, SilentAudioSample[]>();
  private readonly lastRecaptureAtByPeer = new Map<string, number>();
  private readonly inFlight = new Set<string>();
  private readonly nowMs: () => number;

  constructor(private readonly deps: SilentAudioWatchdogDeps) {
    this.nowMs = deps.nowMs ?? (() => Date.now());
  }

  /** Forget the peer's evidence and cooldown; does not cancel a recovery already in progress. */
  public clearPeer(peerId: string): void {
    this.samplesByPeer.delete(peerId);
    this.lastRecaptureAtByPeer.delete(peerId);
    this.inFlight.delete(peerId);
  }

  /**
   * Record measured guest audio and launch recovery after the silent-attached dwell,
   * subject to a per-peer cooldown and in-flight guard. atMs shares the injected
   * clock's millisecond domain and defaults to now; this call does not await recovery.
   */
  public observe(
    peerId: string,
    reading: { audioLevel?: number; audioBitrateBps?: number; atMs?: number },
  ): void {
    const atMs = reading.atMs ?? this.nowMs();
    const sample: SilentAudioSample = {
      atMs,
      audioLevel: reading.audioLevel,
      audioBitrateBps: reading.audioBitrateBps,
    };
    const klass = classifySilentAudioSample(sample);
    if (klass === 'unmeasured') return;

    const window = this.samplesByPeer.get(peerId) ?? [];
    window.push(sample);
    const cutoff = atMs - sampleWindowMs();
    const trimmed = window.filter((s) => s.atMs >= cutoff);
    this.samplesByPeer.set(peerId, trimmed);

    if (klass === 'healthy') return;
    if (!isSilentAttachedPastDwell(trimmed)) return;

    const last = this.lastRecaptureAtByPeer.get(peerId);
    // Only enforce cooldown after a prior attempt (missing key ≠ epoch 0).
    if (last !== undefined && atMs - last < SILENT_AUDIO_RECAPTURE_COOLDOWN_MS) return;
    if (this.inFlight.has(peerId)) return;

    void this.recover(peerId, sample);
  }

  /**
   * Attempt render-path restoration followed by recapture, starting cooldown even
   * on failure. Restoration rejection skips recapture; recovery errors are caught.
   * Clear sample history only when recapture returns a track.
   */
  private async recover(peerId: string, sample: SilentAudioSample): Promise<void> {
    this.inFlight.add(peerId);
    this.lastRecaptureAtByPeer.set(peerId, sample.atMs);
    const detail = {
      peerId,
      audioLevel: sample.audioLevel,
      audioBitrateBps: sample.audioBitrateBps,
      dwellMs: SILENT_AUDIO_DWELL_MS,
    };
    try {
      console.warn(`${DIAG} Silent-but-attached audio past dwell; recovering`, detail);
      sinkWebrtcDebugLine(
        JSON.stringify({
          t: new Date().toISOString(),
          tag: '[AUDIO-BUG-12009] silent-attached',
          ...detail,
        }),
      );
      Sentry.captureMessage('[AUDIO-BUG-12009] Silent-but-attached game audio; recapturing', {
        level: 'warning',
        tags: { audio_stage: 'silent_watchdog', audio_diag: 'AUDIO-BUG-12009' },
        extra: detail,
      });

      await this.deps.ensureAudioCasterDefault?.();
      const track = await this.deps.recaptureGameAudio();
      if (!track) {
        console.error(`${DIAG} Recapture returned null after silent-attached dwell`, { peerId });
        Sentry.captureMessage('[AUDIO-BUG-12009] Silent-audio recapture returned null', {
          level: 'warning',
          tags: { audio_stage: 'silent_watchdog_recapture', audio_diag: 'AUDIO-BUG-12009' },
          extra: { peerId },
        });
      } else {
        this.samplesByPeer.set(peerId, []);
      }
    } catch (error) {
      console.error(`${DIAG} Recovery failed`, { peerId, error });
      Sentry.captureException(error, {
        tags: { audio_stage: 'silent_watchdog', audio_diag: 'AUDIO-BUG-12009' },
      });
    } finally {
      this.inFlight.delete(peerId);
    }
  }
}
